import {
  decodeMeetingMinutesPage,
  type MeetingAttachment,
  type MeetingMinutesDraft,
  type MeetingMinutesPage,
  type MeetingMinutes,
} from './meeting-minutes-model';

export type MeetingFilters = Readonly<{
  search: string;
  dateFrom: string;
  dateTo: string;
  kind: '' | 'daily-safety' | 'committee';
  cadence: '' | 'weekly' | 'monthly';
  cursor: string | null;
}>;

export interface MeetingMinutesRepository {
  list(filters: MeetingFilters): Promise<MeetingMinutesPage>;
  save(draft: MeetingMinutesDraft, expectedVersion: number): Promise<void>;
  complete(id: string, expectedVersion: number): Promise<void>;
  prepareReview(id: string, expectedVersion: number): Promise<Readonly<{ reviewEvidenceId: string; report: MeetingMinutes }>>;
  decide(id: string, expectedVersion: number, action: 'approve' | 'return', reviewEvidenceId: string, opinion: string): Promise<void>;
  archive(id: string, expectedVersion: number, reason: string): Promise<void>;
}

type CallablePort = Readonly<{
  listSites(): Promise<unknown>;
  list(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  execute(input: Readonly<Record<string, unknown>>): Promise<unknown>;
}>;
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const stableValue = (value: unknown): unknown => Array.isArray(value)
  ? value.map(stableValue)
  : value && typeof value === 'object'
    ? Object.fromEntries(Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, stableValue(child)]))
    : value;
async function directMeetingReportHash(value: Record<string, unknown>) {
  const body = {
    meetingId: value.meetingId, tenantId: value.tenantId, siteId: value.siteId,
    contractorId: value.contractorId, contractorEngagementId: value.contractorEngagementId,
    contractorName: value.contractorName, date: value.date, time: value.time, kind: value.kind,
    cadence: value.cadence, type: value.type, place: value.place, title: value.title,
    attendees: value.attendees, agenda: value.agenda, discussion: value.discussion,
    decisions: value.decisions, followUps: value.followUps, attachments: value.attachments,
    submissionVersion: value.submissionVersion, submittedAt: value.submittedAt,
    submittedBy: value.submittedBy,
  };
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(stableValue(body))));
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}

class CallableMeetingMinutesRepository implements MeetingMinutesRepository {
  private summary: Pick<MeetingMinutesPage, 'totalCount' | 'contractorOptions'> | null = null;

  constructor(private readonly port: Promise<CallablePort>, private readonly siteId: string) {}

  async list(filters: MeetingFilters) {
    const page = decodeMeetingMinutesPage(
      await (
        await this.port
      ).list({
        siteId: this.siteId,
        search: filters.search,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        kind: filters.kind,
        cadence: filters.cadence,
        cursor: filters.cursor,
        pageSize: 30,
        includeSummary: filters.cursor === null,
      }),
    );
    if (filters.cursor === null) {
      this.summary = {
        totalCount: page.totalCount,
        contractorOptions: page.contractorOptions,
      };
      return page;
    }
    return this.summary ? { ...page, ...this.summary } : page;
  }

  private async execute(
    operation: 'meeting.upsert' | 'meeting.complete' | 'meeting.prepare-review' | 'meeting.approve' | 'meeting.return' | 'meeting.archive',
    aggregateId: string,
    expectedVersion: number,
    payload: Readonly<Record<string, unknown>>,
  ) {
    const seed = crypto.randomUUID();
    return record(await (
      await this.port
    ).execute({
      commandId: `meeting-${seed}`,
      idempotencyKey: `meeting-${seed}`,
      tenantId: 'kop',
      siteId: this.siteId,
      operation,
      aggregateId,
      expectedVersion,
      payload,
    }));
  }

  save(draft: MeetingMinutesDraft, expectedVersion: number) {
    const evidence: readonly MeetingAttachment[] = draft.attachments;
    return this.execute('meeting.upsert', draft.id, expectedVersion, { ...draft, evidence }).then(() => undefined);
  }

  complete(id: string, expectedVersion: number) {
    return this.execute('meeting.complete', id, expectedVersion, { finalReview: true }).then(() => undefined);
  }

  async prepareReview(id: string, expectedVersion: number) {
    const response = await this.execute('meeting.prepare-review', id, expectedVersion, {});
    const result = record(record(response.receipt).result);
    const rawReport = record(result.report);
    const printEvidence = record(rawReport.printEvidence);
    if (typeof result.reviewEvidenceId !== 'string' || result.version !== expectedVersion ||
        rawReport.meetingId !== id || rawReport.version !== expectedVersion ||
        rawReport.submissionVersion !== expectedVersion || rawReport.status !== 'submitted' ||
        rawReport.siteId !== (this.siteId === 'site-eumseong' ? 'eumseong' : this.siteId) ||
        typeof result.contentHash !== 'string' || !/^[a-f0-9]{64}$/u.test(result.contentHash) ||
        printEvidence.contentHash !== result.contentHash || await directMeetingReportHash(rawReport) !== result.contentHash)
      throw Error('MEETING_REVIEW_EVIDENCE_INVALID');
    const report = decodeMeetingMinutesPage({ complete: true, totalCount: 1, nextCursor: null,
      allowedActions: [], contractorOptions: [], records: [{ ...rawReport, allowedActions: ['print'] }] }).records[0];
    if (!report) throw Error('MEETING_REVIEW_REPORT_INVALID');
    return { reviewEvidenceId: result.reviewEvidenceId, report };
  }

  decide(id: string, expectedVersion: number, action: 'approve' | 'return', reviewEvidenceId: string, opinion: string) {
    return this.execute(`meeting.${action}`, id, expectedVersion, { reviewEvidenceId, opinion }).then(() => undefined);
  }

  archive(id: string, expectedVersion: number, reason: string) {
    return this.execute('meeting.archive', id, expectedVersion, { reason }).then(() => undefined);
  }
}

class UnavailableMeetingMinutesRepository implements MeetingMinutesRepository {
  private unavailable(): never {
    throw new Error('회의록 저장 기능을 현재 사용할 수 없습니다.');
  }
  list(): Promise<MeetingMinutesPage> {
    return Promise.reject(new Error('회의록 목록을 현재 확인할 수 없습니다.'));
  }
  save(): Promise<void> {
    return Promise.reject(this.unavailable());
  }
  complete(): Promise<void> {
    return Promise.reject(this.unavailable());
  }
  prepareReview(): Promise<Readonly<{ reviewEvidenceId: string; report: MeetingMinutes }>> {
    return Promise.reject(this.unavailable());
  }
  decide(): Promise<void> {
    return Promise.reject(this.unavailable());
  }
  archive(): Promise<void> {
    return Promise.reject(this.unavailable());
  }
}

export function isMeetingPreviewRoute(environment: ImportMetaEnv, location: Location): boolean {
  return (
    environment.DEV === true &&
    ['true', '1'].includes(environment.VITE_LOCAL_PREVIEW ?? '') &&
    ['127.0.0.1', 'localhost', '::1'].includes(location.hostname) &&
    location.pathname === '/preview/meetings'
  );
}

class PreviewMeetingMinutesRepository implements MeetingMinutesRepository {
  private records: MeetingMinutes[] = [
    {
      id: 'preview-meeting-1',
      date: '2026-09-01',
      time: '09:00',
      kind: 'committee',
      cadence: 'monthly',
      type: '안전보건협의체-월간',
      place: '음성공장 회의실',
      contractorId: null,
      contractorEngagementId: null,
      contractorName: null,
      title: '9월 안전보건협의체',
      attendees: ['안전관리자', '현장책임자'],
      agenda: '월간 안전보건 현황',
      discussion: '현장 위험요인과 개선 진행상황을 확인했습니다.',
      decisions: '후속조치를 지정된 기한까지 완료합니다.',
      followUps: [
        {
          id: 'preview-action-1',
          content: '통로 정리',
          owner: null,
          dueDate: null,
          status: 'open',
        },
      ],
      attachments: [],
      status: 'draft',
      version: 1,
      approvalRequestId: null,
      printEvidence: null,
      allowedActions: ['edit', 'complete', 'archive', 'print'],
    },
  ];
  async list(): Promise<MeetingMinutesPage> {
    return {
      records: this.records,
      totalCount: this.records.length,
      nextCursor: null,
      allowedActions: ['create'],
      contractorOptions: [],
    };
  }
  async save(draft: MeetingMinutesDraft, expectedVersion: number) {
    const index = this.records.findIndex((record) => record.id === draft.id);
    const next: MeetingMinutes = {
      ...draft,
      status: 'draft',
      version: expectedVersion + 1,
      approvalRequestId: null,
      printEvidence: null,
      allowedActions: ['edit', 'complete', 'archive', 'print'],
    };
    if (index < 0) this.records = [next, ...this.records];
    else this.records = this.records.map((record) => (record.id === draft.id ? next : record));
  }
  async complete(id: string, expectedVersion: number) {
    this.records = this.records.map((record) =>
      record.id === id
        ? {
            ...record,
            status: 'submitted',
            version: expectedVersion + 1,
            allowedActions: ['archive', 'print'],
          }
        : record,
    );
  }
  async prepareReview(): Promise<Readonly<{ reviewEvidenceId: string; report: MeetingMinutes }>> { throw new Error('미리보기에서는 최종 검토를 실행할 수 없습니다.'); }
  async decide(): Promise<void> { throw new Error('미리보기에서는 최종 검토를 실행할 수 없습니다.'); }
  async archive(id: string) {
    this.records = this.records.filter((record) => record.id !== id);
  }
}

function meetingFirebaseConfig() {
  const config = {
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
  };
  return Object.values(config).some((value) => !value.trim()) ? null : config;
}

export type HeadquartersMeetingSite = Readonly<{ siteId: string; displayName: string; kind?: 'factory' | 'marine' | 'construction-site'; reportingDivisionId?: string }>;
export async function loadHeadquartersMeetingSites(expectedActorUid?: string): Promise<readonly HeadquartersMeetingSite[]> {
  if (import.meta.env.VITE_CONTENT_DATA_ADAPTER !== 'firebase') throw Error('MEETING_SITES_UNAVAILABLE');
  const config = meetingFirebaseConfig();
  if (!config) throw Error('MEETING_SITES_UNAVAILABLE');
  const port = await import('@kop/operations-firebase-adapter');
  const response = await port.createMeetingMinutesOperationsPort(config, expectedActorUid).listSites();
  return parseHeadquartersMeetingSites(response);
}

/** Saved meeting metadata, not monthly report calculation or a full ledger read. */
export async function loadHeadquartersRecentMeetings(siteId: string, year: number) {
  const config = meetingFirebaseConfig();
  if (import.meta.env.VITE_CONTENT_DATA_ADAPTER !== 'firebase' || !config ||
      !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId) || !Number.isInteger(year) || year < 2000 || year > 2100)
    throw Error('HQ_MEETINGS_UNAVAILABLE');
  const { createMeetingMinutesOperationsPort } = await import('@kop/operations-firebase-adapter');
  const response = await createMeetingMinutesOperationsPort(config).list({
    siteId, kind: 'committee', cadence: 'monthly', dateFrom: `${year}-01-01`,
    dateTo: `${year}-12-31`, pageSize: 3, includeSummary: false,
  });
  return parseHeadquartersRecentMeetings(response, year);
}

export function parseHeadquartersRecentMeetings(response: unknown, year: number) {
  const value = response as { complete?: unknown; records?: unknown };
  if (value?.complete !== true || !Array.isArray(value.records) || value.records.length > 3)
    throw Error('HQ_MEETINGS_INCOMPLETE');
  const rows = value.records.map((row: Record<string, unknown>) => {
    const id = row?.meetingId ?? row?.id;
    if (typeof id !== 'string' || !id || typeof row.title !== 'string' || !row.title.trim() ||
        typeof row.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/u.test(row.date) || !row.date.startsWith(`${year}-`) ||
        row.kind !== 'committee' || row.cadence !== 'monthly' ||
        !['draft', 'submitted', 'returned', 'completed'].includes(String(row.status)) || !Array.isArray(row.attachments))
      throw Error('HQ_MEETING_INVALID');
    return { id, title: row.title, date: row.date, files: row.attachments.length,
      status: row.status as 'draft' | 'submitted' | 'returned' | 'completed',
      savedAt: typeof row.updatedAt === 'string' && Number.isFinite(Date.parse(row.updatedAt)) ? row.updatedAt : null,
    };
  });
  if (new Set(rows.map(row => row.id)).size !== rows.length) throw Error('HQ_MEETING_INVALID');
  return rows;
}

export function parseHeadquartersMeetingSites(response: unknown): readonly HeadquartersMeetingSite[] {
  const value = response as { complete?: unknown; sites?: unknown };
  if (value?.complete !== true || !Array.isArray(value.sites) || value.sites.some((site) =>
    !site || typeof site !== 'object' ||
    typeof site.siteId !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(site.siteId) ||
    typeof site.displayName !== 'string' || !site.displayName.trim() ||
    (site.kind !== undefined && !['factory', 'marine', 'construction-site'].includes(site.kind)) ||
    (site.reportingDivisionId !== undefined && typeof site.reportingDivisionId !== 'string')) ||
    new Set(value.sites.map((site) => site.siteId)).size !== value.sites.length)
    throw Error('MEETING_SITES_INCOMPLETE');
  return value.sites as readonly HeadquartersMeetingSite[];
}

export function createMeetingMinutesRepository(siteId: string | null): MeetingMinutesRepository {
  if (isMeetingPreviewRoute(import.meta.env, window.location))
    return new PreviewMeetingMinutesRepository();
  if (!siteId || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId))
    return new UnavailableMeetingMinutesRepository();
  if (import.meta.env.VITE_CONTENT_DATA_ADAPTER !== 'firebase')
    return new UnavailableMeetingMinutesRepository();
  const config = meetingFirebaseConfig();
  if (!config)
    return new UnavailableMeetingMinutesRepository();
  return new CallableMeetingMinutesRepository(
    import('@kop/operations-firebase-adapter').then((module) =>
      module.createMeetingMinutesOperationsPort(config),
    ),
    siteId,
  );
}
